import { Component, OnDestroy, viewChild, CUSTOM_ELEMENTS_SCHEMA, ViewChild, inject, ChangeDetectorRef } from '@angular/core';

import { Router, NavigationEnd } from '@angular/router';
import { AudienceService, VeevaTaggedAudience, UnifiedAudience } from '../services/audience.service';
import { FormsModule } from '@angular/forms';
import {
  RdsButtonModule,
  RdsCheckboxModule,
  RdsDialogService,
  RdsDrawerComponent,
  RdsDrawerContainerComponent,
  RdsDrawerContentComponent,
  RdsToastService,
  RdsProgressSpinnerComponent,
  RdsIconsModule,
  RdsPopoverModule,
  RdsBadgeModule,
  RdsFormFieldModule,
} from "@rds/angular-components";
import { Subscription } from "rxjs";
import { filter } from 'rxjs/operators';

import { Publication } from "../models/publication.model";
import { PublicationDetailViewComponent } from "./publication-detail-view/publication-detail-view.component";
import { CTMSMilestone } from "../models/ctmsmilestone.model";
import { CtmsmilestoneDetailViewComponent } from "./ctmsmilestone-detail-view/ctmsmilestone-detail-view.component";
import { ProcessionDialog } from "../procession-dialog/procession-dialog.component";
import { CustomMilestone } from "../models/custom-milestone.model";
import { CreateObject, DataObject, PostBase } from "../models/metadata";
import { CustomMilestoneDetailViewComponent } from "./custom-milestone-detail-view/custom-milestone-detail-view.component";
import { Activity } from "../models/activity.model";
import { VVPMActivity } from "../models/vvpm-activity.model";
import { ActivityDetailViewComponent } from "./activity-detail-view/activity-detail-view.component";

import { UserCreate, UserCreateResponse, UserRaw, UserRawEndpointResponse, UserRawPost, UserRawPostResponse } from '../models/user.model';
import { UserFormComponent } from '../admin-panel/user-form/user-form.component';

// Add Strategic Pillar imports
import { StrategicPillarRaw, StrategicPillarService } from '../services/strategic-pillar.service';
import { StrategicPillarFormComponent } from '../admin-panel/strategic-pillar-form/strategic-pillar-form.component';

// Add Audience imports
import { AudienceRaw } from '../models/audience.model';
import { AudienceFormComponent } from '../admin-panel/audience-form/audience-form.component';
import { VVPMActivityDetailViewComponent } from './vvpm-activity-detail-view/vvpm-activity-detail-view.component';
import { Congress } from '../models/congress.model';
import { CongressDetailViewComponent } from './congress-detail-view/congress-detail-view.component';

import { TimelineDataService } from "../services/timeline-data.service";
import { DetailedViewObjectService } from "../services/detailed-view-object.service";
import { FeedbackService } from "../services/feedback.service";
import { UserService } from '../services/user.service';
import { HttpClient } from '@angular/common/http';
import { AuditService } from '../services/audit.service';
import { AuditEntityType } from '../models/audit.model';

export type FormValidity = "valid" | "warning" | "invalid_priority" | "required" | "delete" | "error";

@Component({
  selector: 'app-detail-view-wrapper',
  standalone: true,
  imports: [
    FormsModule,
    ActivityDetailViewComponent,
    CtmsmilestoneDetailViewComponent,
    CustomMilestoneDetailViewComponent,
    PublicationDetailViewComponent,
    VVPMActivityDetailViewComponent,
    CongressDetailViewComponent,
    UserFormComponent,
    StrategicPillarFormComponent,
    AudienceFormComponent,
    RdsButtonModule,
    RdsCheckboxModule,
    RdsDrawerComponent,
    RdsDrawerContainerComponent,
    RdsDrawerContentComponent,
    RdsIconsModule,
    RdsProgressSpinnerComponent,
    RdsPopoverModule,
    RdsBadgeModule,
    UserFormComponent,
    RdsFormFieldModule
],
  templateUrl: './detail-view-wrapper.component.html',
  styleUrls: ['./detail-view-wrapper.component.scss'],
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class DetailViewWrapperComponent implements OnDestroy {
  private detailedObjectSubscription: Subscription;
  private routeSubscription?: Subscription;
  private readonly router = inject(Router);
  private readonly cdr = inject(ChangeDetectorRef);
  
  detailData!: CreateObject;
  isAdminRoute: boolean = false;
  DETAIL_TYPES = {
    publication: "PUBLICATION",
    ctmsMilestone: "CTMS_MIlESTONE",
    customMilestone: "CUSTOM_MILESTONE",
    activity: "ACTIVITY",
    vvpmActivity: "VVPM_ACTIVITY",
    congress: "CONGRESS",
    planwiseUser: "PLANWISE_USER",
    strategicPillar: "STRATEGIC_PILLAR", // Add Strategic Pillar type
    audience: "AUDIENCE" // Add Audience type
  };
  detailType!: string | undefined;
  drawer = viewChild<RdsDrawerComponent>('drawer');
  @ViewChild(ActivityDetailViewComponent) activityComponent?: ActivityDetailViewComponent;
  @ViewChild(CtmsmilestoneDetailViewComponent) ctmsMilestoneComponent?: CtmsmilestoneDetailViewComponent;
  @ViewChild(CustomMilestoneDetailViewComponent) customMilestoneComponent?: CustomMilestoneDetailViewComponent;
  @ViewChild(PublicationDetailViewComponent) publicationComponent?: PublicationDetailViewComponent;
  isLoadingChanges = false;
  // New properties for export options
  isLoadingExport: boolean = false;
  exportLink: string | null = null;
  selectAllExport: boolean = false;

  constructor(
    private detailedObjectService: DetailedViewObjectService,
    private dialogService: RdsDialogService,
    private timelineDataService: TimelineDataService,
    private feedbackService: FeedbackService,
    private toastService: RdsToastService,
    private userService: UserService,
    private strategicPillarService: StrategicPillarService, // Add Strategic Pillar Service
    private audienceService: AudienceService, // Add Audience Service
    private http: HttpClient, // Add HttpClient for VVPM activity updates
    private auditService: AuditService, // Add Audit Service for view tracking
  ) {
    // Check if we're on admin route
    this.isAdminRoute = this.router.url.includes('/admin');
    
    // Subscribe to route changes
    this.routeSubscription = this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe(() => {
      this.isAdminRoute = this.router.url.includes('/admin');
      this.cdr.markForCheck();
    });
    
    this.detailedObjectSubscription = this.detailedObjectService.detailedObject.subscribe((detailData) => {
      if (!detailData) return;

      if (detailData instanceof Publication) {
        this.detailType = this.DETAIL_TYPES.publication;
        this.detailData = detailData as Publication;
        this.trackEntityView('publication', detailData.metadata.documentNumber, detailData.metadata.title);
      } else if (detailData instanceof CTMSMilestone) {
        this.detailType = this.DETAIL_TYPES.ctmsMilestone;
        this.detailData = detailData as CTMSMilestone;
        this.trackEntityView('ctms_milestone', detailData.metadata.cmID, detailData.metadata.activityName);
      } else if (detailData instanceof CustomMilestone) {
        this.detailType = this.DETAIL_TYPES.customMilestone;
        this.detailData = detailData as CustomMilestone;
        this.trackEntityView('custom_milestone', detailData.ccmID ?? '', detailData.title);
      } else if (detailData instanceof Activity) {
        this.detailType = this.DETAIL_TYPES.activity;
        this.detailData = detailData as Activity;
        this.trackEntityView('custom_activity', detailData.acID ?? '', detailData.title);
      } else if (detailData instanceof VVPMActivity) {
        this.detailType = this.DETAIL_TYPES.vvpmActivity;
        this.detailData = detailData as VVPMActivity;
        this.trackEntityView('vvpm_activity', String(detailData.id), detailData.name__v);
      } else if (detailData instanceof Congress) {
        this.detailType = this.DETAIL_TYPES.congress;
        this.detailData = detailData as Congress;
        this.trackEntityView('congress', String(detailData.id), detailData.displayName);
      } else if ((detailData as any).type === 'EXPORT_OPTIONS') {
        this.detailType = 'EXPORT_OPTIONS';
        const filters = (detailData as any).filters;
        if (!filters.chipValues) {
          filters.chipValues = [];
          // Add timespan as a chip if available
          if (filters.timespan && filters.timespan.start && filters.timespan.end) {
            filters.chipValues.push(
              `Timespan: ${new Date(filters.timespan.start).toLocaleDateString()} - ${new Date(filters.timespan.end).toLocaleDateString()}`
            );
          }
          // Add global filter, if any
          if (filters.global && filters.global.trim().length > 0) {
            filters.chipValues.push(`Global: ${filters.global}`);
          }
          // Add studies if available
          if (filters.studies && Array.isArray(filters.studies) && filters.studies.length > 0) {
            filters.chipValues.push(
                ...filters.studies.map((s: any) => `Study: ${s.studyName || s.studyNumber}`)
            );
        }
        
          
          
          // Add persons if available
          if (filters.persons && Array.isArray(filters.persons) && filters.persons.length > 0) {
            filters.chipValues.push(...filters.persons.map((p: any) => `Person: ${p}`));
          }
          // Add targets if available
          if (filters.targets && Array.isArray(filters.targets) && filters.targets.length > 0) {
            filters.chipValues.push(...filters.targets.map((t: any) => `Target: ${t}`));
          }
        }
        this.detailData = detailData;
      } else if ((detailData instanceof UserRaw)) {
        this.detailType = this.DETAIL_TYPES.planwiseUser;
        this.detailData = detailData;
      } else if ((detailData instanceof StrategicPillarRaw)) { // Add Strategic Pillar detection
        this.detailType = this.DETAIL_TYPES.strategicPillar;
        this.detailData = detailData;
      } else if ((detailData instanceof AudienceRaw)) { // Add Audience detection
        this.detailType = this.DETAIL_TYPES.audience;
        this.detailData = detailData;
      }
      
      // Use a longer timeout to ensure the view is fully ready and component hierarchy is stabilized
      setTimeout(() => {
        const drawerComponent = this.drawer();
        if (drawerComponent) {
          // Force any pending UI updates before opening drawer
          Promise.resolve().then(() => {
            drawerComponent.open();
          });
        } else {
          console.error('Drawer component not found!');
          // Try again with a longer delay if the drawer isn't found
          setTimeout(() => {
            const retryDrawer = this.drawer();
            if (retryDrawer) {
              retryDrawer.open();
            }
          }, 100);
        }
      }, 50);
    });
  }
  
  // Method to handle "Select All" change; sets all export options to the same value
  onSelectAllChange(newValue: boolean): void {
    (this.detailData as any).options.exportActivities = newValue;
    (this.detailData as any).options.exportPublications = newValue;
    (this.detailData as any).options.exportCtmsMilestones = newValue;
    (this.detailData as any).options.exportManualMilestones = newValue;
  }
  
  // Method to trigger the API call for creating the Google Slide
  onExportGslide(): void {
    // Activate loading indicator and clear any previous link
    this.isLoadingExport = true;
    this.exportLink = null;
  
    // Retrieve options and filters from the export object (detailData)
    const options = (this.detailData as any).options;
    const filters = (this.detailData as any).filters;
    const exportFilters = { ...filters, ...options };
  
    // API call to create the Google Slide
    this.timelineDataService.createGoogleSlide(exportFilters).subscribe({
      next: (response: any) => {
        this.isLoadingExport = false; // Turn off loading indicator
        // Assume response.url contains the generated link
        this.exportLink = response.url;
        // Show a success toast
        this.toastService.show('Google Slide created!', 'Success', { timeOut: 5000 });
        // Optionally hide the link after 15 seconds
        setTimeout(() => {
          this.exportLink = null;
        }, 15000);
        // Optionally, close the drawer here
        // this.drawer()?.close();
      },
      error: (err: any) => {
        this.isLoadingExport = false;
        console.error("Error creating Google Slide:", err);
        this.toastService.show('Error creating Google Slide.', 'Error', { timeOut: 5000 });
      }
    });
  }
  
  ngOnDestroy() {
    this.detailedObjectSubscription.unsubscribe();
    this.routeSubscription?.unsubscribe();
  }

  // Called when the drawer is closed; resets scroll position and detailType
  onDrawerClose() {
      document.querySelector('.rds-drawer-body')?.scrollTo(0, 0);
      this.detailType = undefined;
  }

  // Method to delete an object; opens a confirmation dialog and deletes if confirmed
  onDelete(o: DataObject) {
    let dialogRef = this.dialogService.open(ProcessionDialog, { data: { state: 'delete' } });
    dialogRef.afterClosed().subscribe(answer => {
      if (answer) {
        this.isLoadingChanges = true;
        this.detailedObjectService.deleteObject(o).subscribe(answer => {
          this.isLoadingChanges = false;
          if (answer) {
            this.timelineDataService.deleteItem(o);
            if (this.drawer()?.isOpened)
              this.drawer()?.close();
          }
        });
      }
    });
  }

  // Method to submit changes; calls toPost() and posts new data if changes occurred
  async onSubmit(newValue: { data: CreateObject, valid: FormValidity, strategicPillarIds?: number[], audienceIds?: number[], strategicPillars?: number[], audiences?: any[] }) {
    const post = this.detailData.toPost(newValue.data);

    // Normalize possible field names from children
    const submittedStrategic = newValue.strategicPillars ?? newValue.strategicPillarIds;
    const submittedAudiences = newValue.audiences ?? newValue.audienceIds;

    // Derive existing sets from current entity if not submitted (prevents accidental clearing)
    const existingStrategic = (this.detailData as any).strategicPillars ? (this.detailData as any).strategicPillars.map((p: any) => p.id) : [];
    
    // Convert VeevaTaggedAudience to UnifiedAudience format by matching with unified audiences
    let existingAudiences: UnifiedAudience[] = [];
    const rawAudiences: VeevaTaggedAudience[] = (this.detailData as any).audiences || [];
    if (rawAudiences.length > 0 && submittedAudiences === undefined) {
      // Fetch unified audiences to match against
      try {
        const unifiedAudiences = await new Promise<UnifiedAudience[]>((resolve, reject) => {
          this.audienceService.getUnifiedAudiences().subscribe({
            next: (audiences) => resolve(audiences),
            error: (err) => reject(err)
          });
        });
        
        // Match VeevaTaggedAudience to UnifiedAudience by veeva_audience_id/veeva_specialist_id
        existingAudiences = rawAudiences
          .map((a: VeevaTaggedAudience) => {
            // Find matching unified audience based on which ID is not null
            const match = unifiedAudiences.find(ua => 
              (a.veeva_audience_id !== null && ua.veeva_audience_id === a.veeva_audience_id) ||
              (a.veeva_specialist_id !== null && ua.veeva_specialist_id === a.veeva_specialist_id)
            );
            return match;
          })
          .filter(a => a !== undefined) as UnifiedAudience[];
      } catch (err) {
        console.error('Error fetching unified audiences for conversion:', err);
        // Fallback to empty array if fetch fails
        existingAudiences = [];
      }
    }

    (post as any).strategicPillars = submittedStrategic !== undefined ? submittedStrategic : existingStrategic;
    // For audiences, send full objects if available, otherwise send converted existing audiences
    (post as any).audiences = submittedAudiences !== undefined ? submittedAudiences : existingAudiences;

    // Force dataChanged to true for testing purposes
    // This is a temporary fix to ensure the form submission works
    post.dataChanged = true;

    if (newValue.valid == 'valid' && post.dataChanged) {
      this.postNewData(post);
    } else if (newValue.valid != 'valid' && post.dataChanged) {
      let dialogRef = this.dialogService.open(ProcessionDialog, { data: { state: newValue.valid } });
      dialogRef.afterClosed().subscribe(answer => {
        if (answer)
          this.postNewData(post);
      });
    } else {
      if (this.drawer()?.isOpened) {
        this.drawer()?.close();
      }
      this.feedbackService.emitInfo("No changes detected");
    }
  }

  // Method to post new data; updates the timeline on success
  /**
   * Track entity view for audit trail (fire-and-forget)
   */
  private trackEntityView(entityType: AuditEntityType, entityId: string, entityTitle?: string): void {
    this.auditService.trackView(entityType, entityId, entityTitle);
  }

  private async postNewData(post: PostBase) {
    this.isLoadingChanges = true;
    this.detailedObjectService.postObject(post)?.subscribe(async (res) => {
      if (res.success) {
        let updatedEntityData = res.updatedData!;
        // Backend now returns strategicPillars & audiences; ensure minimal fallback if missing
        if (!(updatedEntityData as any).strategicPillars && (post as any).strategicPillars) {
          (updatedEntityData as any).strategicPillars = (post as any).strategicPillars.map((id: number) => ({ id }));
        }
        // Audiences are returned as full objects from backend, no fallback needed
        this.timelineDataService.updateItem(updatedEntityData);
        
        // Trigger refresh of strategic pillars and audiences in filter dropdowns
        // This ensures newly tagged pillars/audiences appear in the filter lists
        this.strategicPillarService.triggerRefresh();
        this.audienceService.triggerRefresh();
        
        if (this.drawer()?.isOpened) {
          this.drawer()?.close();
        }
      }
      this.isLoadingChanges = false;
    });
  }

  onSubmitVVPMActivity(newValue: { data: VVPMActivity, strategicPillarIds: number[] }) {
    this.isLoadingChanges = true;
    const vvpmActivity = newValue.data;
    
    // POST to /api/vvpm-activities/edit with the required payload structure
    // Note: Audiences are read-only from Veeva and cannot be edited
    this.http.post<{ success: boolean, message?: string, updatedData?: VVPMActivity }>('/api/vvpm-activities/edit', {
      values: {
        id: vvpmActivity.id,
        isVisible: vvpmActivity.isVisible,
        strategicPillars: newValue.strategicPillarIds
      }
    }).subscribe({
      next: (res: { success: boolean, message?: string, updatedData?: VVPMActivity }) => {
        this.isLoadingChanges = false;
        if (res.success) {
          // Update the timeline with the new data
          if (res.updatedData) {
            this.timelineDataService.updateItem(Object.assign(new VVPMActivity(), res.updatedData));
          } else {
            // If backend doesn't return updated activity, use local data
            this.timelineDataService.updateItem(vvpmActivity);
          }
          
          // Trigger refresh of strategic pillars in filter dropdowns
          // This ensures newly tagged pillars appear in the filter lists
          this.strategicPillarService.triggerRefresh();
          
          this.feedbackService.emitSuccess('VVPM activity updated successfully');
          
          if (this.drawer()?.isOpened) {
            this.drawer()?.close();
          }
        } else {
          this.feedbackService.emitError(res.message || 'Failed to update VVPM activity');
        }
      },
      error: (err: any) => {
        this.isLoadingChanges = false;
        this.feedbackService.handleError('updateVVPMActivity', err);
      }
    });
  }

  onDeleteUser(user: UserRaw) {
    const dialogRef = this.dialogService.open(ProcessionDialog, { data: { state: 'delete' } });
    
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.isLoadingChanges = true;
        this.userService.deleteUser(user).subscribe({
          next: (success: boolean) => {
            this.isLoadingChanges = false;
            if (success) {
              this.userService.listUsers()
            } else {
              console.error('Failed to delete user.');
            }
          },
          error: (err) => {
            this.isLoadingChanges = false;
            console.error('Error deleting user:', err);
          }
        });
      }
    });
  }

  onSubmitUser(newValue: { data: UserRaw, valid: FormValidity }) {
    if ((this.detailData as UserRaw)?.unixID == undefined) {
      const put = newValue.data as unknown as UserCreate;
      this.createUser(put);
    } else {
      const post = (this.detailData as UserRaw).toPost(newValue.data);

      if (newValue.valid == 'valid' && post.dataChanged) {
        this.updateUser(post);
      } else if (newValue.valid != 'valid' && post.dataChanged) {
        let dialogRef = this.dialogService.open(ProcessionDialog, { data: { state: newValue.valid } });
        dialogRef.afterClosed().subscribe(answer => {
          if (answer){ 
            this.updateUser(post) }
        });
      } else {
        if (this.drawer()?.isOpened) {
          this.drawer()?.close();
        }
        this.feedbackService.emitInfo("No changes detected");
      }
    }
  }

  // Add Strategic Pillar submit method
  onSubmitStrategicPillar(newValue: { data: StrategicPillarRaw, valid: FormValidity }) {
    if ((this.detailData as StrategicPillarRaw)?.id === 0) {
      // Create new pillar
      this.createStrategicPillar(newValue.data);
    } else {
      // Update existing pillar
      if (newValue.valid == 'valid') {
        this.updateStrategicPillar(newValue.data);
      } else {
        let dialogRef = this.dialogService.open(ProcessionDialog, { data: { state: newValue.valid } });
        dialogRef.afterClosed().subscribe(answer => {
          if (answer){ 
            this.updateStrategicPillar(newValue.data);
          }
        });
      }
    }
  }

  // Add Strategic Pillar delete method
  onDeleteStrategicPillar(pillar: StrategicPillarRaw) {
    const dialogRef = this.dialogService.open(ProcessionDialog, { data: { state: 'delete' } });
    
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.isLoadingChanges = true;
        this.strategicPillarService.deletePillar(pillar.id).subscribe({
          next: () => {
            this.isLoadingChanges = false;
            this.strategicPillarService.triggerRefresh();
            if (this.drawer()?.isOpened) {
              this.drawer()?.close();
            }
          },
          error: (err) => {
            this.isLoadingChanges = false;
            console.error('Error deleting strategic pillar:', err);
          }
        });
      }
    });
  }

  // Add Audience submit method
  onSubmitAudience(newValue: { data: AudienceRaw, valid: FormValidity }) {
    if ((this.detailData as AudienceRaw)?.id === 0) {
      // Create new audience
      this.createAudience(newValue.data);
    } else {
      // Update existing audience
      if (newValue.valid == 'valid') {
        this.updateAudience(newValue.data);
      } else {
        let dialogRef = this.dialogService.open(ProcessionDialog, { data: { state: newValue.valid } });
        dialogRef.afterClosed().subscribe(answer => {
          if (answer){ 
            this.updateAudience(newValue.data);
          }
        });
      }
    }
  }

  // Add Audience delete method
  onDeleteAudience(audience: AudienceRaw) {
    const dialogRef = this.dialogService.open(ProcessionDialog, { data: { state: 'delete' } });
    
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.isLoadingChanges = true;
        this.audienceService.deleteAudience(audience.id).subscribe({
          next: () => {
            this.isLoadingChanges = false;
            this.audienceService.triggerRefresh();
            if (this.drawer()?.isOpened) {
              this.drawer()?.close();
            }
          },
          error: (err) => {
            this.isLoadingChanges = false;
            console.error('Error deleting audience:', err);
          }
        });
      }
    });
  }

  private createUser(put: UserCreate) {
    this.isLoadingChanges = true;
    this.userService.createUser(put)?.subscribe({
      next: (success: UserCreateResponse) => {
        this.isLoadingChanges = false;
        if (success) {
          if (this.drawer()?.isOpened) {
            this.drawer()?.close();
          }
          // this.userService.listUsers();
        }
      },
      error: (err) => {
        this.isLoadingChanges = false;
        console.error('Error creating user:', err);
        this.feedbackService.emitError("Unable to create user. Please try again later.");
      }
    });
  }
  
  private updateUser(updateUser: UserRawPost) {
    this.isLoadingChanges = true;
    this.userService.updateUser(updateUser)?.subscribe({
      next: (success: UserRawPostResponse) => {
        this.isLoadingChanges = false;
        if (success) {
          if (this.drawer()?.isOpened) {
            this.drawer()?.close();
          }
          // this.userService.listUsers();
        }
      },
      error: (err) => {
        this.isLoadingChanges = false;
        console.error('Error updating user:', err);
        this.feedbackService.emitError("Unable to save changes. Please try again after refreshing the page.");
      }
    });
  }

  // Add Strategic Pillar CRUD methods
  private createStrategicPillar(pillar: StrategicPillarRaw) {
    this.isLoadingChanges = true;
    this.strategicPillarService.createPillar(pillar).subscribe({
      next: () => {
        this.isLoadingChanges = false;
        this.strategicPillarService.triggerRefresh();
        if (this.drawer()?.isOpened) {
          this.drawer()?.close();
        }
      },
      error: (err) => {
        this.isLoadingChanges = false;
        console.error('Error creating strategic pillar:', err);
        this.feedbackService.emitError("Unable to create strategic pillar. Please try again later.");
      }
    });
  }

  private updateStrategicPillar(pillar: StrategicPillarRaw) {
    this.isLoadingChanges = true;
    this.strategicPillarService.updatePillar(pillar.id, pillar).subscribe({
      next: () => {
        this.isLoadingChanges = false;
        this.strategicPillarService.triggerRefresh();
        if (this.drawer()?.isOpened) {
          this.drawer()?.close();
        }
      },
      error: (err) => {
        this.isLoadingChanges = false;
        console.error('Error updating strategic pillar:', err);
        this.feedbackService.emitError("Unable to save changes. Please try again after refreshing the page.");
      }
    });
  }

  // Add Audience CRUD methods
  private createAudience(audience: AudienceRaw) {
    this.isLoadingChanges = true;
    this.audienceService.createAudience(audience).subscribe({
      next: () => {
        this.isLoadingChanges = false;
        this.audienceService.triggerRefresh();
        if (this.drawer()?.isOpened) {
          this.drawer()?.close();
        }
      },
      error: (err) => {
        this.isLoadingChanges = false;
        console.error('Error creating audience:', err);
        this.feedbackService.emitError("Unable to create audience. Please try again later.");
      }
    });
  }

  private updateAudience(audience: AudienceRaw) {
    this.isLoadingChanges = true;
    this.audienceService.updateAudience(audience.id, audience).subscribe({
      next: () => {
        this.isLoadingChanges = false;
        this.audienceService.triggerRefresh();
        if (this.drawer()?.isOpened) {
          this.drawer()?.close();
        }
      },
      error: (err) => {
        this.isLoadingChanges = false;
        console.error('Error updating audience:', err);
        this.feedbackService.emitError("Unable to save changes. Please try again after refreshing the page.");
      }
    });
  }
}
